
import { Box, Button, FormLabel, Grid, Link, Paper, useTheme } from "@mui/material";
import { UIRefreshCategoryEnum } from "../../common/enums/UIRefreshCategoryEnum";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { IEntityValueTankModel } from "../../common/models/IEntityValueModels";
import { getTankRoute } from "../../common/utilities/RouteHelpers";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useAutoRefresh } from "../../hooks/use-auto-refresh/UseAutoRefresh";
import ObjectTagValues, { ObjectTagValuesMode } from "../object-tag-values/ObjectTagValues";
import TankCylinder from "../tank-cylinder/TankCylinder";
import TankBatteryTagValueUtils from "./TankBatteryTagValueUtils";
import { DateRange, DisplaySettings } from "@mui/icons-material";
import { Ref, forwardRef, useEffect, useImperativeHandle } from "react";
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from "react-i18next";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";
import { ResourceTypeEnum } from "../../common/enums/ResourceTypeEnum";
import { ITankBattery } from "../../common/models/ITankBattery";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useCustomerAttribute } from "../../queries/attributes/UseCustomerAttribute";
import { useTagValues } from "../../queries/tag-values/UseTagValues";
import { TANK_BATTERY_ALTERNATE_PRESENTATION } from "../../services/attribute-data-service/Models";
import OfflineDataService from "../../services/offline-data-service/OfflineDataService";
import { ITrendingDisplayOptions } from "../../services/trending-data-service/Models";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import { CustomDurationDialog, CustomDurationDialogValues, bindCustomDurationDialog, useCustomDurationDialogState } from "../custom-duration-dialog/CustomDurationDialog";
import { DurationOptionEnum } from "../custom-duration-dialog/CustomDurationUtils";
import LoadingZone from "../loading-zone/LoadingZone";
import { ObjectGraphOptionsDialog, bindObjectGraphOptionsDialog, useObjectGraphOptionsDialogState } from "../object-graph/options-dialog/ObjectGraphOptionsDialog";
import { ObjectGraphOptionsDialogValues } from "../object-graph/options-dialog/ObjectGraphOptionsUtils";
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import TankBatteryLevelGraph from "../tank-battery-level-graph/TankBatteryLevelGraph";
import useTankBatteryTagValuesState from "./TankBatteryTagValuesState";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import { addDays } from "date-fns";
import TankCylinderV2 from "../tank-cylinder-v2/TankCylinderV2";
import { IeLynxObject } from "../../common/models/eLynxObject";

const tileVariant = "elevation";
const tileElevation = 3;

export type TankBatteryTagValuesProps = {
    visible?: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
    groupID: number;
    contextObject: IeLynxObject;
    dense?: boolean;
    designMode?: boolean;
};

export type TankBatteryTagValuesType = {
    executeAction: (action: BaseActionEnum) => void;
};

function TankBatteryTagValues({
    visible,
    setViewOptionState,
    groupID,
    contextObject,
    dense,
    designMode
}: TankBatteryTagValuesProps, ref: Ref<unknown> | undefined) {
    //const routeContext = useRouteContext();
    const navigateTo = useNavigateTo();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { t } = useTranslation();
    const optionsDialog = useObjectGraphOptionsDialogState();
    const durationDialog = useCustomDurationDialogState();

    const {
        state: {
            options,
            isOptionsDirty
        },
        resetOptions,
        setDuration,
        setGraphOptions
    } = useTankBatteryTagValuesState();

    const offlineConfig = OfflineDataService.getOfflineConfig();

    useImperativeHandle<unknown, TankBatteryTagValuesType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const theme = useTheme();

    const isPhone = isMobile && !isTablet;

    const { data, refetch, status } = useQueryNotifications(useTagValues)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        objectID: contextObject.id,
        objectType: contextObject.objectType,
        date: undefined,
        offline: offlineState.offline,
        useLKVOffline: offlineConfig.useLKV
    });

    const { data: tankBatteryAlternatePresentation } = useQueryNotifications(useCustomerAttribute)({
        groupID: groupID,
        name: TANK_BATTERY_ALTERNATE_PRESENTATION,
    });

    const useAlternatePresentation = tankBatteryAlternatePresentation?.attributeValue?.toLowerCase() === 'true' && !isPhone;

    const tanks = data ? TankBatteryTagValueUtils.shapeTankData(data as ITankBattery) : [];

    const oilTanks = tanks.filter(o => o.subTypeID === ResourceTypeEnum.Oil);
    const waterTanks = tanks.filter(o => o.subTypeID === ResourceTypeEnum.Water);

    const bothTankTypes = oilTanks.length > 0 && waterTanks.length > 0;
    const multipleTanks = oilTanks.length > 1 || waterTanks.length > 1;

    useEffect(() => {
        if (visible) {
            const viewOptionState = isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            setViewOptionState && setViewOptionState(viewOptionState);
        }
    }, [
        visible,
        isOptionsDirty,
        setViewOptionState
    ]);

    function onRefreshData() {

        if (options.durationOptions.option !== DurationOptionEnum.Custom) {

            let dayOffset = -1;

            switch (options.durationOptions.option) {
                //case DurationOptionEnum.OneDay:
                //    break;

                case DurationOptionEnum.ThreeDays:
                    dayOffset = -3;
                    break;

                case DurationOptionEnum.SevenDays:
                    dayOffset = -7;
                    break;

                case DurationOptionEnum.ThirtyDays:
                    dayOffset = -30;
                    break;

                default:
                    break;
            }

            const endDate = new Date();
            const startDate = addDays(endDate, dayOffset);

            setDuration({...options.durationOptions, 
                duration: {
                    startDate: startDate,
                    endDate: endDate
            }});

         }

         refetch();
    }


    // AutoRefresh support
    useAutoRefresh({
        dataRefresh: () => {

            if (visible && !designMode) {
                onRefreshData();
            }
        },
        category: UIRefreshCategoryEnum.Details
    });

    const onNavigate = (tank: IEntityValueTankModel, shiftKey: boolean, ctrlKey: boolean) => {

        if (designMode) {
            return;
        }

        let options: NavigateToOptions | undefined;

        if (shiftKey || ctrlKey) {
            options = {
                newViewType: shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
            };
        }

        const url = getTankRoute(UIViewEnum.Tank_Details, groupID, undefined, contextObject.id, tank.id);
        navigateTo(url, options);
    };

    function executeAction(action: BaseActionEnum) {

        switch (action) {
            case BaseActionEnum.Refresh:
                refetch();
                break;
        }
    }

    function renderTank(index: number, tank: IEntityValueTankModel) {
        return (
            <Paper
                sx={{
                    padding: theme.spacing(1),
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    '& + &': {
                        marginTop: theme.spacing(1)
                    },
                }}
                key={index}
                variant={tileVariant}
                elevation={tileElevation}
            >

                <Link
                    sx={{
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        color: 'inherit',
                        textAlign: 'center',
                    }}
                    onClick={(ev) => onNavigate(tank, ev.shiftKey, ev.ctrlKey)} >
                    {tank.displayText}
                </Link>
                
                <TankCylinderV2 tank={tank} designMode={designMode} dense={dense} />
            </Paper>

        );
    }

    async function onOverrideSettings() {

        const result = await optionsDialog.open({
            groupID: groupID,
            contextObject: contextObject,
            contextObjectType: contextObject.objectType,
            displayOptions: { ...options.graphDisplayOptions },
            hideShowAdditional: true,
            hideShowStacked: true
        });

        if (result.reason === "ok") {
            const values = result.values as ObjectGraphOptionsDialogValues;
            setGraphOptions(values.displayOptions as ITrendingDisplayOptions);
        }
    };

    async function onCustomDuration() {
        const result = await durationDialog.open(options.durationOptions);

        if (result.reason === "ok") {
            const values = result.values as CustomDurationDialogValues;

            if (values.option === DurationOptionEnum.Default) {
                setDuration({
                    option: DurationOptionEnum.Default,
                    duration: {
                        startDate: undefined,
                        endDate: undefined
                    }
                });

            } else {
                setDuration(values);
            }
        }
    }

    return (
        <>
            {visible &&
                <LoadingZone isLoading={status === "loading"}>
                    <ContentContainer>
                        <ContentSection>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: '1 1 auto',
                                    overflow: "hidden",
                                    overflowY: 'auto',
                                    padding: theme.spacing(1),
                                    backgroundColor: theme.palette.custom.background.cardContainer
                                }}>

                                {
                                !offlineState.offline && !dense &&
                                <Paper
                                    sx={{
                                        display: "flex",
                                        flexDirection: 'column',
                                        flex: "0 0 45%"
                                    }}
                                    variant={tileVariant}
                                    elevation={tileElevation}
                                >
                                    <TankBatteryLevelGraph
                                        dateRange={options.durationOptions.duration}
                                        displayOptions={options.graphDisplayOptions}
                                    />
                                </Paper>
                                }


                                <Grid
                                    container
                                    id='tbgrid'
                                    spacing={1}
                                    sx={{
                                        flexWrap: useAlternatePresentation ? 'nowrap' : undefined,
                                        flex: "1 1 auto",
                                        marginTop: theme.spacing(1)
                                    }}>

                                    {!offlineState.offline && useAlternatePresentation &&

                                        <>
                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}
                                                size={{
                                                    xs: 12,
                                                    sm: 6
                                                }}>
                                                {
                                                    (oilTanks.length ? oilTanks : waterTanks).map((o, index) =>
                                                        <Grid
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                maxHeight: multipleTanks ? '50%' : undefined,
                                                                marginBottom: theme.spacing(1),
                                                                flexBasis: 'auto'
                                                            }}
                                                            size={12}>
                                                            {renderTank(index, o)}
                                                        </Grid>
                                                    )
                                                }
                                            </Grid>

                                            {
                                                bothTankTypes &&
                                                <Grid
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column'
                                                    }}
                                                    size={{
                                                        xs: 12,
                                                        sm: 6
                                                    }}>
                                                    {(waterTanks).map((o, index) =>
                                                        <Grid
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                maxHeight: multipleTanks ? '50%' : undefined,
                                                                marginBottom: theme.spacing(1),
                                                                flexBasis: 'auto!important',
                                                                flexGrow: '1!important'
                                                            }}
                                                            size={12}>
                                                            {renderTank(index, o)}
                                                        </Grid>
                                                    )}

                                                </Grid>
                                            }
                                        </>
                                    }

                                    {!offlineState.offline && !useAlternatePresentation && tanks.map((o, index) =>

                                    // sm={dense ? 12 : undefined}
                                        <Grid
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                maxWidth: dense ? 'unset!important' : undefined,

                                                [theme.breakpoints.only('sm')]: {
                                                    maxWidth: '100%!important',
                                                    flexBasis: '100%!important',
                                                }
                                            }}
                                            size={{
                                                xs: 12,
                                                lg: 6,
                                                xl: 4
                                            }}>
                                            {renderTank(index, o)}
                                        </Grid>
                                    )}

                                    {!useAlternatePresentation && !dense && <Grid
                                        sx={{
                                            display: 'flex',
                                            alignSelf: useAlternatePresentation ? 'flex-end' : undefined
                                        }}
                                        style={isPhone || tanks.length % 2 ? undefined : {
                                            //marginTop: theme.spacing(2) 
                                        }}
                                        size={{
                                            xs: 12,

                                            lg: !useAlternatePresentation 
                                                ? 12 - ((tanks.length % 2) * 6)
                                                : 12,

                                            xl: !useAlternatePresentation 
                                                ? 12 - ((tanks.length % 3) * 4)
                                                : 12
                                        }}>
                                        {
                                            data &&
                                            <ObjectTagValues
                                                visible={true}
                                                grouped={offlineState.offline ? true : false}
                                                mode={ObjectTagValuesMode.Values}
                                                autoHeight={true}
                                                noOptionsSideSheet={true}
                                                variant={tileVariant}
                                                elevation={tileElevation}
                                            />
                                        }
                                    </Grid>
                                    }
                                </Grid>

                                {useAlternatePresentation && !dense &&
                                    <Box
                                        sx={{
                                            display: 'flex'
                                        }}

                                        style={isPhone || tanks.length % 2 ? undefined : {
                                            //marginTop: theme.spacing(2) 
                                        }}>
                                        {
                                            data &&
                                            <ObjectTagValues
                                                visible={true}
                                                grouped={offlineState.offline ? true : false}
                                                mode={ObjectTagValuesMode.Values}
                                                autoHeight={true}
                                                noOptionsSideSheet={true}
                                                variant={tileVariant}
                                                elevation={tileElevation}
                                            />
                                        }
                                    </Box>
                                }
                            </Box>
                        </ContentSection>

                        {!dense && 
                        <OptionsSideSheet onReset={() => resetOptions()}>
                            <Button variant='text'
                                startIcon={<DisplaySettings />}
                                sx={{ justifyContent: 'flex-start' }}
                                onClick={onOverrideSettings}
                            >
                                {t('trending.overrideOptions.title')}
                            </Button>
                            <FormLabel component="legend">{t('customDuration.duration')}</FormLabel>
                            <Button variant='text'
                                startIcon={<DateRange />}
                                sx={{ justifyContent: 'flex-start' }}
                                onClick={onCustomDuration}
                            >
                                {options.durationLabel}
                            </Button>
                        </OptionsSideSheet>
                        }
                    </ContentContainer>
                </LoadingZone>
            }
            {!dense && <ObjectGraphOptionsDialog
                //contextObjectType={contextObject.objectType}
                {...bindObjectGraphOptionsDialog(optionsDialog)}
            />}
            {!dense && <CustomDurationDialog
                overrideDefaultText={t('customDuration.configured')}
                value={options.durationOptions}
                {...bindCustomDurationDialog(durationDialog)}
            />}
        </>
    );
}

export default forwardRef(TankBatteryTagValues);